import { Icon } from '@natera/material/lib/icon'
import { useCallback, useMemo, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { cn } from '~/shared/lib/cn'
import { highlightText } from '~/shared/lib/highlight-text'
import { Popover, PopoverContent, PopoverTrigger, SearchView, Text } from '~/shared/ui'
import { usePayors } from '../api'

interface SuggestedPayorFieldProps {
  label: string
  name?: string
  value?: string
  onChange: (value: string) => void
  onBlur: () => void
}

export function SuggestedPayorField({
  label,
  name,
  value,
  onChange,
  onBlur,
}: SuggestedPayorFieldProps) {
  const [searchValue, setSearchValue] = useState('')
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const {
    data: {
      data: { insurancePayors: payors },
    },
  } = usePayors()

  const selectedPayor = payors.find((p) => p.payorId === value)

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value)
  }, [])

  const visiblePayors = useMemo(() => {
    if (searchValue) {
      return payors.filter((payor) => {
        return (
          payor.payorName.toLowerCase().includes(searchValue.toLowerCase()) ||
          payor.payorId.toLowerCase().includes(searchValue.toLowerCase())
        )
      })
    }
    return payors
  }, [payors, searchValue])

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
    >
      <PopoverTrigger
        role="combobox"
        data-track-name={name}
        data-field-name={name}
        className="text-text-primary group relative w-full border-none bg-transparent p-0 outline-none"
      >
        <label
          className={cn(
            'text-text-secondary bg-surface-lowest absolute top-1/2 left-3 flex -translate-y-1/2 items-center gap-1 px-1 text-[15px] leading-5 tracking-[0.25px] whitespace-nowrap transition-all duration-150 ease-in-out',
            'top-0 translate-y-0 text-xs leading-4 tracking-[0.4px]',
            'group-data-[state=open]:text-primary-main group-focus:text-primary-main',
          )}
        >
          {label}
        </label>
        <div
          className={cn(
            'mt-2 flex h-[44px] cursor-pointer items-center justify-between gap-2 rounded-sm px-[15px] transition-colors',
            'border-input-outlined-enabled-border hover:border-input-outlined-hovered-border group-data-[state=open]:border-input-outlined-focused-border group-data-[state=open]:hover:border-input-outlined-focused-border group-focus:border-input-outlined-focused-border group-focus:hover:border-input-outlined-focused-border border group-focus:border-2 group-data-[state=open]:border-2',
            'group-focus:px-[14px] group-data-[state=open]:px-[14px]',
          )}
        >
          <Text
            className="text-text-primary"
            appearance="BODY2"
            truncated
            withTooltip
          >
            {selectedPayor ? selectedPayor.payorName : 'Payor'}
          </Text>
          <Icon
            children="arrow_drop_down"
            className="text-content-high-emphasis group-data-[state=open]:rotate-180"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={0}
        triggerWidthInherited
      >
        <SearchView
          value={searchValue}
          onChange={handleSearchChange}
          placeholder="Search"
          forceOpen
          scrollable={false}
        >
          {visiblePayors.length > 0 ? (
            <Virtuoso
              className="min-h-[40dvh]"
              data={visiblePayors}
              itemContent={(_, payor) => (
                <button
                  type="button"
                  className={cn(
                    'hover:bg-action-hovered focus:bg-action-hovered flex w-full cursor-pointer flex-col border-none bg-transparent px-3 py-2 text-left outline-none',
                    {
                      'bg-primary-selected': value === payor.payorId,
                    },
                  )}
                  key={payor.payorId}
                  onClick={() => {
                    onChange(payor.payorId)
                    setIsPopoverOpen(false)
                    onBlur()
                  }}
                >
                  <Text appearance="BODY2">{highlightText(payor.payorName, searchValue)}</Text>
                  <Text
                    className="text-text-secondary"
                    appearance="CAPTION"
                  >
                    {highlightText(payor.payorId, searchValue)}
                  </Text>
                </button>
              )}
            />
          ) : (
            <Text
              className="box-border w-full px-4 py-3"
              appearance="SUBTITLE"
            >
              No Results
            </Text>
          )}
        </SearchView>
      </PopoverContent>
    </Popover>
  )
}
